import { useCallback, useState } from 'react';
import { navigate } from 'gatsby';
import { logStatsigEvent } from 'src/app/atoms/AbTesting/statsigClient';
import { submitHubSpotFormBuilder, SubmissionValuesData } from 'src/functions/hubspot/hubspot.service';
import { useBookACallFormContext } from 'src/hooks/useBookACallFormContext';
import useHubspotFormCreation from 'src/hooks/useHubspotFormCreation';

const FORM_ID = '869f3119-6ef6-46e8-98c0-ecac1cb520b5';

interface BookACallFormHandler {
  formId: string;
  isLoading: boolean;
  isSubmitted: boolean;
  submitForm: () => void;
  submitFormAndRedirect: () => void;
}

const useBookACallFormHandler = (formWrapperId: string): BookACallFormHandler => {
  const { isSubmitted, markAsSubmitted, setPendingEmail, consumePendingEmail } = useBookACallFormContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const submitForm = useCallback(() => {
    const submitHubSpotForm = submitHubSpotFormBuilder(`#${formWrapperId}`);
    submitHubSpotForm();
  }, [formWrapperId]);

  const submitFormAndRedirect = useCallback(() => {
    const emailInput = document.querySelector<HTMLInputElement>(`#${formWrapperId} input[name="email"]`);
    if (emailInput?.value) {
      setPendingEmail(emailInput.value);
    }
    setIsLoading(true);
    submitForm();
  }, [submitForm, formWrapperId, setPendingEmail]);

  const handleFormSubmitted = useCallback(
    (_form: HTMLFormElement, data: { redirectUrl: string; submissionValues: SubmissionValuesData }) => {
      setIsLoading(false);
      markAsSubmitted();
      logStatsigEvent('request_a_call_clicked', 1);

      const email = consumePendingEmail() || data.submissionValues.email || '';
      const encodedEmail = encodeURIComponent(email);
      navigate(`/request-a-call-form?rac_email=${encodedEmail}`);
    },
    [markAsSubmitted, consumePendingEmail]
  );

  useHubspotFormCreation({
    formId: FORM_ID,
    formWrapperId,
    onFormReadyCallback: () => null,
    onFormSubmitCallback: () => {
      // Capture email for Enter key submissions via the active element
      const activeInput = document.activeElement as HTMLInputElement;
      if (activeInput?.name === 'email' && activeInput.value) {
        setPendingEmail(activeInput.value);
      }
    },
    onFormSubmittedCallback: handleFormSubmitted,
  });

  return {
    formId: FORM_ID,
    isLoading,
    isSubmitted,
    submitForm,
    submitFormAndRedirect,
  };
};

export default useBookACallFormHandler;
